import { useState } from 'react';
import {
  Button,
  CopyField,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Spinner,
} from '@shared/ui';
import { generatePassword } from '@shared/lib/password';
import type { UUID } from '@shared/api';
import { useSetPassword } from '../model/mutations';

export function SetPasswordDialog({
  open,
  onOpenChange,
  manufacturerId,
  email,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  manufacturerId: UUID;
  email?: string | null;
}) {
  const [password, setPassword] = useState(() => generatePassword());
  const [issued, setIssued] = useState(false);
  const setPasswordMut = useSetPassword(manufacturerId);

  const reset = () => {
    setPassword(generatePassword());
    setIssued(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = () => {
    setPasswordMut.mutate(password, { onSuccess: () => setIssued(true) });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{issued ? 'Пароль выдан' : 'Выдать новый пароль'}</DialogTitle>
        </DialogHeader>

        {!issued ? (
          <div className="space-y-3">
            <FormField label="Новый пароль" htmlFor="new-password">
              <div className="flex gap-2">
                <Input
                  id="new-password"
                  type="text"
                  className="font-mono"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPassword(generatePassword())}
                  title="Сгенерировать"
                >
                  🎲
                </Button>
              </div>
            </FormField>

            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-foreground/80">
              ⚠️ Все активные сессии вендора будут завершены. Ему придётся залогиниться заново.
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Отмена
              </Button>
              <Button
                variant="destructive"
                onClick={submit}
                disabled={setPasswordMut.isPending || password.length < 8}
              >
                {setPasswordMut.isPending ? <Spinner /> : 'Выдать пароль'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Скопируйте и передайте вендору. Пароль больше нигде не отобразится.
            </p>
            <div className="space-y-2 rounded-md border bg-muted/30 p-3">
              {email ? <CopyField label="Email" value={email} /> : null}
              <CopyField label="Пароль" value={password} />
            </div>
            <DialogFooter>
              <Button onClick={close}>Готово</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
